/**
 * SortableList — wrapper riusabile su dnd-kit per liste verticali riordinabili
 * via drag&drop con handle. Include keyboard accessibility (frecce + spazio)
 * out-of-the-box.
 *
 * Usage:
 *   <SortableList items={items} onReorder={(newItems) => ...}>
 *     {(item, sortableProps) => (
 *       <div ref={sortableProps.setNodeRef} style={sortableProps.style} ...>
 *         <button {...sortableProps.attributes} {...sortableProps.listeners} className="cursor-grab">
 *           <GripVertical />
 *         </button>
 *         ... contenuto item ...
 *       </div>
 *     )}
 *   </SortableList>
 */
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function SortableItem({ id, children }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : "auto",
  };
  return children({ setNodeRef, style, attributes, listeners, isDragging });
}

export default function SortableList({ items, getId, onReorder, children }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((i) => getId(i) === active.id);
    const newIdx = items.findIndex((i) => getId(i) === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    onReorder(arrayMove(items, oldIdx, newIdx));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map(getId)} strategy={verticalListSortingStrategy}>
        {items.map((item) => (
          <SortableItem key={getId(item)} id={getId(item)}>
            {(sortableProps) => children(item, sortableProps)}
          </SortableItem>
        ))}
      </SortableContext>
    </DndContext>
  );
}
